import { QueryClient } from "@tanstack/react-query";

import type { PublishedNavigationElement } from "@/lib/api";
import { publishedNavigationElementsQuery } from "@/features/dashboard/features/dashboard/hooks/queries";
import { getNavigationElementBySlug } from "@/features/shared/navigation/navigation.utils";

/** Lookups (cached one included) before a slug counts as unknown. */
const DEFAULT_ATTEMPTS = 3;
/** Pause between a miss and the next fetch. */
const DEFAULT_RETRY_DELAY_MS = 200;

const delay = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

const notFound = () => new Response("", { status: 404 });

/**
 * Fetches the published navigation of a dashboard, always over the network.
 *
 * `staleTime: 0` on purpose: the app-wide default is 100s, so the cached tree
 * can still be the previous publication. Callers here need the tree as the
 * backend has it now. Concurrent calls still share one request.
 */
export function fetchPublishedNavigationElements(
  queryClient: QueryClient,
  dashboardId: string,
): Promise<PublishedNavigationElement[]> {
  const { queryKey, queryFn } = publishedNavigationElementsQuery(dashboardId);

  return queryClient.fetchQuery({ queryKey, queryFn, staleTime: 0 });
}

/**
 * Resolves a page slug against the dashboard's published navigation, retrying
 * while the slug is absent.
 *
 * Publishing a newly created page is not instantly visible on the published
 * endpoints, and the CMS links into the dashboard with the draft slug (the
 * chevron must open the page that was being edited). A single lookup would turn
 * that lag into a hard 404 that only a full reload recovers from.
 *
 * Throws a 404 `Response` (router error boundary) once the attempts are spent,
 * so genuinely unknown slugs still fail.
 */
export async function ensurePublishedNavigationElement(
  queryClient: QueryClient,
  dashboardId: string,
  pageSlug: string | undefined,
  subPageSlug: string | undefined,
  options?: { attempts?: number; retryDelayMs?: number },
): Promise<PublishedNavigationElement> {
  if (!pageSlug) throw notFound();

  const attempts = options?.attempts ?? DEFAULT_ATTEMPTS;
  const retryDelayMs = options?.retryDelayMs ?? DEFAULT_RETRY_DELAY_MS;

  for (let attempt = 0; attempt < attempts; attempt++) {
    if (attempt > 0) await delay(retryDelayMs);

    // Navigating between pages of a dashboard is the common case and must stay
    // free, so the first look is at the cache. Only a slug the cached tree does
    // not know is worth going to the network for.
    const navigationElements =
      attempt === 0
        ? await queryClient.ensureQueryData(publishedNavigationElementsQuery(dashboardId))
        : await fetchPublishedNavigationElements(queryClient, dashboardId);
    const element = getNavigationElementBySlug(navigationElements, pageSlug, subPageSlug);

    if (element) return element;
  }

  throw notFound();
}
